import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { PendingApproval } from "@/components/bridgette/BridgetteApprovalDialog";

export type BrainAction = {
  type: string;
  parameters: Record<string, any>;
};

export type ExecuteResult = {
  /** Final spoken response after execution (may be augmented). */
  spoken: string;
  /** True if execution was deferred pending user approval. */
  pendingApproval?: boolean;
};

type AssistantLanguage = "en" | "ru";

const COPY = {
  en: {
    confirmOnScreen: "Please confirm on screen to proceed.",
    needNameOrAddress: "I need a name or address to open that job.",
    couldntFindJob: (search: string) => `I couldn't find a job for ${search}.`,
    openingJob: (name: string) => `Opening ${name}'s job.`,
    openingTab: (tab?: string) => `Opening ${tab} tab.`,
    openingForm: (formType?: string) => `Opening ${formType?.replace("_", " ")} form.`,
    couldntOrderMeasurement: "I couldn't order the measurement.",
    measurementOrdered: "Measurement ordered.",
    hitError: (message?: string) => `I hit an error: ${message || "unknown"}`,
  },
  ru: {
    confirmOnScreen: "Подтвердите это на экране, чтобы продолжить.",
    needNameOrAddress: "Мне нужно имя клиента или адрес, чтобы открыть эту работу.",
    couldntFindJob: (search: string) => `Я не смогла найти работу для ${search}.`,
    openingJob: (name: string) => `Открываю работу ${name}.`,
    openingTab: (tab?: string) => `Открываю вкладку ${tab}.`,
    openingForm: (formType?: string) => `Открываю форму ${formType?.replace("_", " ")}.`,
    couldntOrderMeasurement: "Не удалось заказать замер.",
    measurementOrdered: "Замер заказан.",
    hitError: (message?: string) => `У меня возникла ошибка: ${message || "неизвестно"}`,
  },
} as const;

const APPROVAL_REQUIRED = new Set([
  "create_job",
  "create_lead",
  "create_task",
  "schedule_appointment",
]);

async function getAuth() {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  const userId = sessionData?.session?.user?.id;
  if (!token || !userId) throw new Error("Session expired. Please refresh and sign in.");
  const { data: profile } = await supabase
    .from("profiles")
    .select("org_id")
    .eq("id", userId)
    .single();
  if (!profile?.org_id) throw new Error("Organization context missing.");
  return { token, userId, orgId: profile.org_id as string };
}

/**
 * Executes a Bridgette brain action locally.
 * - Read-only / nav / form actions execute immediately.
 * - create_* / schedule_* actions are queued for user approval.
 *
 * No Lovable AI is used anywhere in this hook.
 */
export function useBridgetteActionExecutor() {
  const navigate = useNavigate();
  const [pendingApproval, setPendingApproval] = useState<PendingApproval | null>(null);

  const runApproved = useCallback(
    async (p: PendingApproval) => {
      try {
        const { token, userId, orgId } = await getAuth();
        const params = p.parameters || {};

        switch (p.type) {
          case "create_job": {
            const { data, error } = await supabase.functions.invoke("bridgette-create-job", {
              body: { ...params, org_id: orgId },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (error || !data?.job_id) {
              toast.error(`Failed to create job: ${error?.message || "Unknown error"}`);
              return;
            }
            toast.success(data.job_number ? `Job ${data.job_number} created` : "Job created");
            navigate(`/jobs/${data.job_id}`);
            return;
          }
          case "create_lead": {
            const { data, error } = await supabase.functions.invoke("bridgette-create-lead", {
              body: { ...params, lead_source: "voice_assistant", org_id: orgId },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (error || !data?.lead_id) {
              toast.error(`Failed to create lead: ${error?.message || "Unknown error"}`);
              return;
            }
            toast.success(`Lead created${data.contact_name ? ` for ${data.contact_name}` : ""}`);
            navigate(`/leads/${data.lead_id}`);
            return;
          }
          case "create_task": {
            const { data, error } = await supabase.functions.invoke("bridgette-create-task", {
              body: { ...params, created_by: userId, org_id: orgId },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (error) {
              toast.error(`Failed to create task: ${error?.message || "Unknown error"}`);
              return;
            }
            toast.success(`Task created${data?.title ? `: ${data.title}` : ""}`);
            return;
          }
          case "schedule_appointment": {
            const { data, error } = await supabase.functions.invoke("bridgette-schedule-appointment", {
              body: {
                job_id: params.job_id,
                lead_id: params.lead_id,
                appointment_type: params.appointment_type,
                scheduled_date: params.scheduled_date,
                duration_minutes: params.duration_minutes || 60,
                notes: params.notes,
              },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (error) {
              toast.error(`Failed to schedule appointment: ${error?.message || "Unknown error"}`);
              return;
            }
            toast.success("Appointment scheduled");
            navigate("/calendar");
            return;
          }
        }
      } catch (e: any) {
        toast.error(e?.message || "Failed to execute action");
      }
    },
    [navigate],
  );

  const approve = useCallback(() => {
    if (!pendingApproval) return;
    const p = pendingApproval;
    setPendingApproval(null);
    void runApproved(p);
  }, [pendingApproval, runApproved]);

  const cancel = useCallback(() => {
    setPendingApproval(null);
    toast.info("Action cancelled");
  }, []);

  /**
   * Execute an action returned by bridgette-brain.
   * Returns the final spoken text (may be unchanged) and a flag if approval is pending.
   */
  const execute = useCallback(
    async (
      action: BrainAction | null,
      spokenFromBrain: string,
      lang: AssistantLanguage = "en",
    ): Promise<ExecuteResult> => {
      const copy = COPY[lang];
      if (!action) return { spoken: spokenFromBrain };
      const { type, parameters = {} } = action;

      // ---- Approval-gated actions ----
      if (APPROVAL_REQUIRED.has(type)) {
        const summary = spokenFromBrain || `Confirm ${type.replace("_", " ")}`;
        setPendingApproval({
          type: type as PendingApproval["type"],
          parameters,
          spokenSummary: summary,
        });
        return {
          spoken: `${spokenFromBrain ? spokenFromBrain + " " : ""}${copy.confirmOnScreen}`,
          pendingApproval: true,
        };
      }

      // ---- Immediate actions ----
      try {
        switch (type) {
          case "navigate": {
            const path = parameters.path || parameters.route;
            if (typeof path === "string" && path.length > 0) navigate(path);
            return { spoken: spokenFromBrain };
          }
          case "open_job": {
            const { token, orgId } = await getAuth();
            const search = parameters.owner_name || parameters.search || parameters.job_name || parameters.address;
            if (!search) {
              return { spoken: copy.needNameOrAddress };
            }
            const { data, error } = await supabase.functions.invoke("get-job-by-name", {
              body: { search_term: search, org_id: orgId },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (error || !data?.job_id) {
              return { spoken: copy.couldntFindJob(String(search)) };
            }
            navigate(`/jobs/${data.job_id}`);
            return { spoken: copy.openingJob(String(data.owner_name || search)) };
          }
          case "navigate_to_tab":
          case "switch_job_tab": {
            const tab = parameters.tab;
            if (tab) {
              window.dispatchEvent(new CustomEvent("bridgette-change-tab", { detail: { tab } }));
            }
            return { spoken: spokenFromBrain || copy.openingTab(typeof tab === "string" ? tab : undefined) };
          }
          case "open_form": {
            const formType = parameters.form_type;
            if (formType) {
              window.dispatchEvent(new CustomEvent("bridgette-open-form", { detail: { formType } }));
            }
            return {
              spoken:
                spokenFromBrain ||
                copy.openingForm(typeof formType === "string" ? formType : undefined),
            };
          }
          case "order_measurement": {
            const { token } = await getAuth();
            const { data, error } = await supabase.functions.invoke("bridgette-get-measurements", {
              body: { job_id: parameters.job_id, address: parameters.address },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (error) {
              toast.error(`Failed to order measurement: ${error.message}`);
              return { spoken: copy.couldntOrderMeasurement };
            }
            toast.success("Measurement ordered");
            return { spoken: data?.spoken_response || copy.measurementOrdered };
          }
          default:
            console.warn("Unknown action type:", type);
            return { spoken: spokenFromBrain };
        }
      } catch (e: any) {
        toast.error(e?.message || "Action failed");
        return { spoken: copy.hitError(e?.message) };
      }
    },
    [navigate],
  );

  return { execute, pendingApproval, approve, cancel };
}
